/**
 * Keeps the cart in step between this browser and the customer's account.
 *
 * A Vuex plugin rather than component code: the cart is edited from several
 * screens, and syncing from each of them would mean every new caller has to
 * remember. Subscribing to the mutations catches all of them, including ones
 * added later.
 *
 * THE RULES
 * ---------
 *   pull, on sign-in       -> reconcile, newest wins
 *   push, on every change  -> debounced
 *   forget, on sign-out    -> local only, never the server
 *
 * Deliberately NOT a merge. Merging two carts silently reintroduces lines the
 * customer had just deleted on their other device, with nothing to show them
 * that is what happened. Losing an edit is obvious and recoverable; a
 * resurrected line that gets paid for is neither.
 *
 * Every network call fails soft. The cart has always worked from localStorage
 * and must keep working when the endpoint is unavailable — a customer must
 * never be blocked from checking out because a convenience feature is down.
 */

import CartService from "@/services/cart.service";

/**
 * How long to wait after the last change before pushing.
 *
 * Adding several sites in a row is one intent, not five, and the panel lets
 * you do it quickly. Long enough to collapse a burst, short enough that
 * closing the tab straight after an edit still saves it.
 */
const PUSH_DEBOUNCE_MS = 1200;

/** Mutations that mean the customer changed the cart and it should be pushed. */
const PUSH_ON = new Set(["cart/addToCart", "cart/removeFromCart", "cart/saveCart", "cart/clearCart"]);

export function createCartSync() {
  return (store) => {
    let pushTimer = null;
    let pulling = false;

    const isLoggedIn = () => Boolean(store.state.auth?.status?.loggedIn);

    /** ms since the epoch, or 0 for an absent/unparseable stamp. */
    const stampOf = (iso) => {
      const parsed = iso ? Date.parse(iso) : Number.NaN;
      return Number.isNaN(parsed) ? 0 : parsed;
    };

    async function push() {
      if (!isLoggedIn()) return;
      try {
        const { data } = await CartService.saveCart(store.state.cart.items);
        // Adopt the server's timestamp so the next comparison is against what
        // the server actually recorded, not what this browser guessed.
        store.commit("cart/adoptRemoteCart", {
          items: store.state.cart.items,
          updatedAt: data?.updated_at ?? store.state.cart.updatedAt,
        });
      } catch (error) {
        // Soft failure: the cart still lives in localStorage and the next edit
        // retries. Logged rather than surfaced — the customer has done nothing
        // wrong and there is nothing for them to act on.
        console.warn("[cart] could not save to the account", error);
      }
    }

    function schedulePush() {
      if (!isLoggedIn()) return;
      clearTimeout(pushTimer);
      pushTimer = setTimeout(push, PUSH_DEBOUNCE_MS);
    }

    /**
     * Reconciles this browser's cart with the account's.
     *
     * Runs on sign-in and on a hard reload while signed in.
     */
    async function pull() {
      if (!isLoggedIn() || pulling) return;
      pulling = true;

      try {
        const { data } = await CartService.getSavedCart();
        const remoteItems = Array.isArray(data?.items) ? data.items : [];
        const remoteAt = stampOf(data?.updated_at);

        const localItems = store.state.cart.items;
        const localAt = stampOf(store.state.cart.updatedAt);

        // Nothing anywhere.
        if (remoteItems.length === 0 && localItems.length === 0) return;

        // Only the server has one — a new browser, or a cleared one. This is
        // the case the whole feature exists for.
        if (localItems.length === 0 && remoteItems.length > 0) {
          store.commit("cart/adoptRemoteCart", {
            items: remoteItems,
            updatedAt: data?.updated_at ?? null,
          });
          return;
        }

        // Only this browser has one — first sync for this account, or the
        // customer emptied it elsewhere and has since started a new one here.
        if (remoteItems.length === 0 && localItems.length > 0) {
          await push();
          return;
        }

        // Both. Newest wins.
        //
        // A local cart with no stamp predates this feature, so it cannot be
        // compared: the server's copy is the one with a known age and takes it.
        if (remoteAt > localAt) {
          store.commit("cart/adoptRemoteCart", {
            items: remoteItems,
            updatedAt: data?.updated_at ?? null,
          });
        } else {
          await push();
        }
      } catch (error) {
        console.warn("[cart] could not load the saved cart", error);
      } finally {
        pulling = false;
      }
    }

    // --- wiring ------------------------------------------------------------

    store.subscribe((mutation) => {
      if (PUSH_ON.has(mutation.type)) schedulePush();

      // Signing out must clear this browser's copy but leave the account's
      // alone — the customer still wants their cart next time.
      if (mutation.type === "auth/logout") {
        clearTimeout(pushTimer);
        store.commit("cart/forgetLocalCart");
      }

      // A fresh sign-in is the moment to reconcile.
      if (mutation.type === "auth/loginSuccess") pull();
    });

    // Already signed in on load (a reload, or a returning session).
    if (isLoggedIn()) pull();

    /**
     * A pending push would be lost if the tab closed inside the debounce
     * window, which is exactly when someone adds a last item and navigates
     * away. Flushed synchronously via sendBeacon-style fire-and-forget.
     */
    window.addEventListener("pagehide", () => {
      if (pushTimer === null || !isLoggedIn()) return;
      clearTimeout(pushTimer);
      // No await: the page is going away. A rejected promise here is expected
      // and must not surface as an unhandled rejection.
      push().catch(() => {});
    });
  };
}
